import { useState } from "react";
import { motion } from "framer-motion";
import { MailQuestion, Copy, Check, LogOut, RefreshCw, Loader2, FileText } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { BrandMark } from "@/components/BrandMark";

// Shown to a worker whose dashboard is completely empty — i.e. their account
// isn't linked to any coordinator's company yet. Guides them to ask their
// coordinator to add their email, rather than leaving them staring at a blank app.
export const NotLinkedGate = ({ onRecheck, rechecking }) => {
  const { user, logout } = useAuth();
  const [copied, setCopied] = useState(false);
  const email = user?.email || "";

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(email);
      setCopied(true);
      toast.success("Email copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy — please copy it manually");
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="not-linked-gate">
      <header className="border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <BrandMark />
          <Button variant="ghost" size="sm" data-testid="gate-logout-btn" className="rounded-full text-muted-foreground" onClick={logout}>
            <LogOut className="w-4 h-4 mr-1.5" /> Sign out
          </Button>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md text-center"
        >
          <div className="mx-auto w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-6">
            <MailQuestion className="w-8 h-8 text-primary" />
          </div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">
            You're not linked to a company yet
          </h1>
          <p className="mt-3 text-muted-foreground text-base leading-relaxed">
            Your account isn't connected to a company on Cross Jobs yet, so there are no jobs,
            rosters or timesheets to show yet. Ask your coordinator to add the email
            below to their crew, then check again.
          </p>

          <div className="mt-6 flex items-center gap-2 rounded-xl border border-border bg-card p-2 pl-4 text-left" data-testid="gate-email-box">
            <span className="flex-1 font-mono text-sm truncate">{email}</span>
            <Button variant="secondary" size="sm" data-testid="gate-copy-email-btn" className="rounded-lg shrink-0" onClick={copyEmail}>
              {copied ? <Check className="w-4 h-4 mr-1.5 text-emerald-600" /> : <Copy className="w-4 h-4 mr-1.5" />}
              {copied ? "Copied" : "Copy"}
            </Button>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Button
              data-testid="gate-recheck-btn"
              className="flex-1 rounded-full bg-primary hover:bg-primary/90 text-white h-11"
              onClick={onRecheck}
              disabled={rechecking}
            >
              {rechecking ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              I've been added — check again
            </Button>
            <Button variant="outline" data-testid="gate-signout-btn" className="rounded-full h-11" onClick={logout}>
              Sign out
            </Button>
          </div>

          <p className="mt-6 text-xs text-muted-foreground">
            Coordinators can add you from the <span className="font-medium">Crew</span> tab using this email.
          </p>

          <div className="mt-8 rounded-2xl border border-border bg-card p-5 text-left" data-testid="gate-guide-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                <FileText className="w-5 h-5 text-primary" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-sm">Learn the app while you wait</p>
                <p className="text-xs text-muted-foreground">A quick guide to clocking on, jobs, photos and your roster.</p>
              </div>
            </div>
            <a
              data-testid="gate-worker-guide-link"
              href="/Worker-Manual.pdf?v=2"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full border border-border px-4 py-2.5 text-sm font-semibold hover:bg-secondary transition-colors"
            >
              <FileText className="w-4 h-4" /> Read the worker guide (PDF)
            </a>
          </div>
        </motion.div>
      </main>
    </div>
  );
};
